<script lang="ts">
	import type { ClassValue } from "svelte/elements";
	import { ButtonVariant } from "../../../core/schema/ButtonVariant.js";
	import type { Notification } from "@core/schema/Notification.js";
	import { NotificationColor } from "@core/schema/NotificationColor.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import Button from "./common/Button.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Slideshow from "./common/Slideshow.svelte";

	const { notifications }: { notifications: Notification[] } = $props();

	let currentIndex = $state(0);
	let isResetting = $state(false);

	const DURATION_MS = 500;

	const colorToClassMap: Record<NotificationColor, ClassValue> = {
		[NotificationColor.Blue]: tw("bg-[#214E92]"),
		[NotificationColor.Navy]: tw("bg-cmcnavy"),
		[NotificationColor.Orange]: tw("bg-cmcwarning"),
	};

	$effect(() => {
		if (notifications.length <= 1) {
			return;
		}

		if (currentIndex === notifications.length) {
			const id = setTimeout(() => {
				isResetting = true;
				currentIndex = 0;

				requestAnimationFrame(() => {
					isResetting = false;
				});
			}, DURATION_MS);

			return (): void => {
				clearTimeout(id);
			};
		}

		return undefined;
	});
</script>

<div class="relative h-12 md:h-16 overflow-hidden w-full empty:hidden">
	{#each [...notifications, ...notifications.slice(0, 1)] as notification, index (index)}
		<a
			class={[
				"group flex flex-row items-center md:px-6 gap-3 md:gap-8 absolute inset-0",
				"shadow-[0px_50px_32px_-40px_rgba(0,0,0,0.1)] backdrop-blur-[8px]",
				"transition-transform group/underline",
				isResetting ? "duration-0" : "duration-500",
				"will-change-transform",
				colorToClassMap[notification.color],
			]}
			style:transform={`translate3d(0, ${(index - currentIndex) * 100}%, 0)`}
			href={notification.link}
		>
			<div class="hidden lg:flex flex-row items-center gap-1 shrink-0">
				<span class="text-sm font-semibold leading-none text-white">{(index % notifications.length) + 1}</span>
				<span class="text-sm font-light leading-none text-white">/</span>
				<span class="text-sm font-semibold leading-none text-white">{notifications.length}</span>
			</div>

			<div
				class="flex-1 flex-col flex items-start text-2xs/[1.2] sm:text-xs/[1.2] md:text-sm/[1.2] text-white grow max-md:pl-3"
			>
				<div class="sm:truncate font-bold mb-1">
					<Formatted text={notification.headline} />
				</div>
				<div
					class="max-md:overflow-hidden max-xl:[mask-image:linear-gradient(to_right,black_0%,black_92%,transparent_100%)] max-xl:[-webkit-mask-image:linear-gradient(to_right,black_0%,black_92%,transparent_100%)]"
				>
					{#key currentIndex}
						<Slideshow
							engine="transform"
							flowDirection="left"
							speed={0.5}
							class="max-xl:pr-3 gap-2"
							endTimeout={7500}
							forceSlidingOnMobile
							onEnd={(): void => {
								if (notifications.length > 1 && currentIndex % notifications.length === index % notifications.length) {
									currentIndex++;
								}
							}}
						>
							<div class="font-light line-clamp-1">
								<Formatted text={notification.text} />
							</div>
						</Slideshow>
					{/key}
				</div>
			</div>
			<Button variant={ButtonVariant.Text} type="span" icon="arrow" class="text-white shrink-0">
				<Formatted text="hover[[{notification.linkText}]]" />
			</Button>
		</a>
	{/each}
</div>
